import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Turnstile, { turnstileEnabled } from '../components/Turnstile';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  // Bumped after a failed submit to force a fresh Turnstile token (single-use).
  const [captchaResetKey, setCaptchaResetKey] = useState(0);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }
    if (turnstileEnabled && !captchaToken) {
      setError('Please complete the verification below');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_BASE_URL}/auth/forgot-password`, {
        email: email.trim(),
        captcha_token: captchaToken,
      });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Something went wrong. Please try again.');
      setCaptchaToken('');
      setCaptchaResetKey((k) => k + 1);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-grid container">
        <div className="login-hero d-none d-md-flex">
          <div className="login-hero-content">
            <div className="brand">
              <img
                src="https://froomyrsdtspkmaognle.supabase.co/storage/v1/object/public/logos/zilch-testing-store.myshopify.com-1753009531626.png"
                className="border border-white rounded-3"
                alt="Zilch Logo"
                width={48}
                height={48}
              />
              <span>Zilch.</span>
            </div>
            <h2>Measure and manage your product carbon footprint.</h2>
            <p>Reset your password and get straight back to your product library and PCF assessments.</p>
          </div>
          <div className="login-orb orb-1" />
          <div className="login-orb orb-2" />
          <div className="login-orb orb-3" />
        </div>

        <div className="login-card-wrapper">
          <div className="login-card">
            <div className="brand brand-center d-md-none mb-3">
              <img
                src="https://froomyrsdtspkmaognle.supabase.co/storage/v1/object/public/logos/zilch-testing-store.myshopify.com-1753009531626.png"
                className="border border-white rounded-3"
                alt="Zilch Logo"
                width={40}
                height={40}
              />
              <span>Zilch.</span>
            </div>

            {sent ? (
              <>
                <h1 className="login-title">Check your inbox</h1>
                <p className="login-subtitle">
                  If an account exists for <strong>{email.trim()}</strong>, we've sent a link to reset your
                  password. It expires in an hour.
                </p>
                <p className="login-subtitle mt-3">
                  Didn't get it? Check your spam folder, or{' '}
                  <button
                    type="button"
                    className="btn btn-link p-0 align-baseline zilch-text-green"
                    onClick={() => {
                      setSent(false);
                      setCaptchaToken('');
                      setCaptchaResetKey((k) => k + 1);
                    }}
                  >
                    try a different email
                  </button>
                  .
                </p>
                <Link to="/login" className="login-button d-block text-center text-decoration-none mt-3">
                  Back to sign in
                </Link>
              </>
            ) : (
              <>
                <h1 className="login-title">Forgot your password?</h1>
                <p className="login-subtitle">
                  Enter your email and we'll send you a link to set a new one.
                </p>

                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label>Email Address</label>
                    <input
                      type="email"
                      className="login-input"
                      placeholder="your.email@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="email"
                      autoFocus
                      required
                    />
                  </div>

                  <Turnstile
                    key={captchaResetKey}
                    action="forgot-password"
                    onVerify={setCaptchaToken}
                    onExpire={() => setCaptchaToken('')}
                  />

                  <button type="submit" className="login-button" disabled={submitting || !email.trim()}>
                    {submitting ? 'Sending…' : 'Send reset link'}
                  </button>
                </form>

                {error && (
                  <div className="login-error" role="alert">
                    {error}
                  </div>
                )}

                <div className="text-center mt-4">
                  <small className="text-white">
                    Remembered it?{' '}
                    <Link to="/login" className="zilch-text-green">
                      Sign in
                    </Link>
                  </small>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;
